import { Dispatch } from 'react';
import { NextRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

import { redirect } from 'shared/helpers';
import { SearchPageType, SearchSource } from '../SearchContext';
import {
    buildQueryParamsString,
    buildUrlFromPathAndQueryParams
} from './index';
import { MAIN_SITE_LINK } from '../../constants';

const LIST_PAGE_SIZE = '25';
const MAPS_PAGE_SIZE = '100';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeMostFalsyValues = (obj: any) => {
    // removes: null, undefined, ''
    const result = Object.entries(obj).reduce((arr, curr) => {
        const [key, value] = curr;
        const isNull = value === null;
        const isUndefined = value === undefined;
        const isEmptyString = value === '';

        if (isNull || isUndefined || isEmptyString) {
            return arr;
        }

        return {
            ...arr,
            [key]: value
        };
    }, {});

    return result;
};

export const getPropertySubType = (
    propertyType: string | null,
    propertySubType: string[],
    isBungalow: boolean
) => {
    if (propertyType === 'houses') {
        if (propertySubType.length === 1) {
            // If sub-type is bungalow by itself then it is in the path and should not appear
            // in the query string
            return isBungalow ? '' : propertySubType;
        }

        if (propertySubType.length > 1) {
            return propertySubType.join('&property_sub_type=');
        }
    }

    return '';
};

type Beds = {
    beds_min: string;
    beds_max: string;
    buttonTitle: string;
};

export const getBedsParams = (beds: Beds) => {
    const { beds_min, beds_max } = beds;

    const shouldAddBedsMin = beds_min !== '' && beds_min !== 'No min';
    const shouldAddBedsMax = beds_max !== '' && beds_max !== 'No max';

    const bedsMin = shouldAddBedsMin ? beds_min : '';
    const bedsMax = shouldAddBedsMax ? beds_max : '';

    return {
        beds_min: bedsMin,
        beds_max: bedsMax
    };
};

export const getPriceFrequencyParam = (
    searchType: SearchPageType,
    priceFrequency: string
) => {
    const isRent = searchType === 'to-rent';
    if (!isRent) {
        return '';
    }

    if (priceFrequency === 'per_week') {
        return 'per_week';
    }

    return 'per_month';
};

type LettingType = {
    queryParamKey: string;
    checked: boolean;
};

type LettingTypes = {
    shortLets: LettingType;
    petsAllowed: LettingType;
    billsIncluded: LettingType;
};

export const getLettingTypeParams = (lettingTypes: LettingTypes) => {
    const result = Object.entries(lettingTypes).reduce((acc, curr) => {
        const [key, value] = curr;
        const { checked, queryParamKey } = value;

        if (checked) {
            const isShortLet = key === 'shortLets';
            const newValue = !isShortLet ? 'true' : 'short';

            return {
                ...acc,
                [queryParamKey]: newValue
            };
        }

        return acc;
    }, {});

    type ResultType = {
        bills_included: string;
        pets_allowed: string;
        rental_term: string;
    };

    return result as ResultType;
};

const getMaxPriceParam = (
    priceMaxMonthly = '0',
    priceMaxWeekly = '0',
    priceMax = '0'
) => {
    if (priceMaxMonthly !== '0' && priceMaxMonthly !== '') {
        return priceMaxMonthly;
    }

    if (priceMaxWeekly !== '0' && priceMaxWeekly !== '') {
        return priceMaxWeekly;
    }

    return priceMax !== '0' && priceMax !== '' ? priceMax : '';
};

const getMinPriceParam = (
    priceMinMonthly = '0',
    priceMinWeekly = '0',
    priceMin = '0'
) => {
    if (priceMinMonthly !== '0' && priceMinMonthly !== '') {
        return priceMinMonthly;
    }

    if (priceMinWeekly !== '0' && priceMinWeekly !== '') {
        return priceMinWeekly;
    }

    return priceMin !== '0' && priceMin !== '' ? priceMin : '';
};

type Price = {
    price_min: string;
    price_max: string;
    buttonTitle: string;
};
type PriceMonthly = {
    price_min_monthly: string;
    price_max_monthly: string;
    buttonTitle: string;
};
type PriceWeekly = {
    price_min_weekly: string;
    price_max_weekly: string;
    buttonTitle: string;
};

export const getPriceParams = (
    price: Price,
    priceMonthly: PriceMonthly,
    priceWeekly: PriceWeekly
) => {
    const { price_max_monthly, price_min_monthly } = priceMonthly;
    const { price_max_weekly, price_min_weekly } = priceWeekly;
    const { price_max, price_min } = price;

    const maxPrice = getMaxPriceParam(
        price_max_monthly,
        price_max_weekly,
        price_max
    );
    const minPrice = getMinPriceParam(
        price_min_monthly,
        price_min_weekly,
        price_min
    );

    type ResultType = {
        price_max: string;
        price_min: string;
    };

    const result = {
        price_max: maxPrice,
        price_min: minPrice
    };

    return result as ResultType;
};

const getPolyEnc = (polyEnc?: string | string[]) => {
    if (!polyEnc) {
        return '';
    }

    if (typeof polyEnc === 'string') {
        return polyEnc;
    }

    if (Array.isArray(polyEnc)) {
        return polyEnc.join('&polyenc=');
    }

    return '';
};

type searchPathBuilder = {
    host: string;
    searchType: string;
    maps: boolean;
    propertyType: string;
    extra: string[];
};

const getNewPath = (builder: searchPathBuilder): string => {
    const slug = [builder.host, builder.searchType];
    if (builder.maps) slug.push('map');
    slug.push(builder.propertyType, ...builder.extra);

    return slug.join('/');
};

const getPathBuilder = (
    asPath: string,
    searchType: SearchPageType
): searchPathBuilder => {
    const path = asPath.split('?').shift() || '';
    const splitPath = path.split('/');

    const hasSearchTypeInPath = splitPath.includes(searchType);
    const hasMapInPath = splitPath.includes('map');

    if (!hasSearchTypeInPath)
        return {
            host: '',
            searchType,
            maps: hasMapInPath,
            propertyType: 'property',
            extra: []
        };

    const newPathArray = splitPath.slice(0, 3);

    return {
        host: newPathArray[0],
        searchType: newPathArray[1],
        maps: hasMapInPath,
        propertyType: hasMapInPath ? newPathArray[3] : newPathArray[2],
        extra: []
    };
};

export const updateQueryParams = (
    // eslint-disable-next-line
    filters: any,
    searchType: SearchPageType,
    searchSource: SearchSource,
    sortOrder: string,
    isPropertyTypeFlatsOrHouses: boolean,
    isBungalow: boolean,
    userAlertId: string | null,
    excludeRadius: boolean,
    locationText: string,
    isMaps: boolean,
    polyEnc?: string | string[],
    excludePageSize?: boolean
) => {
    const {
        location,
        radius,
        propertyType,
        propertySubType,
        propertyAdded,
        features,
        keywords,
        availableFrom,
        includeLet,
        includeShared,
        furnishings,
        beds,
        price,
        priceFrequency,
        priceMonthly,
        priceWeekly,
        duration,
        transportType,
        underOfferOrSold,
        chainFree,
        priceReduced,
        shortLets,
        billsIncluded,
        petsAllowed,
        helpToBuy,
        newHomes,
        retirementHome,
        auction,
        isSharedOwnership
    } = filters;
    const bedsParams = getBedsParams(beds);
    const priceParams = getPriceParams(price, priceMonthly, priceWeekly);

    const includeIsAuction =
        (isPropertyTypeFlatsOrHouses && auction !== '') ||
        (!isPropertyTypeFlatsOrHouses && auction === 'false');

    const includeIsRetirementHome =
        (isPropertyTypeFlatsOrHouses && retirementHome !== '') ||
        (!isPropertyTypeFlatsOrHouses && retirementHome === 'false');

    const includeNewHomesValue = newHomes === 'exclude';

    const pageSize = isMaps ? MAPS_PAGE_SIZE : LIST_PAGE_SIZE;

    // NOTE: The order of these filters matters for SEO purposes
    // Mostly alphabetical with a few exceptions
    const queryParams: ParsedUrlQuery = {
        added: propertyAdded ? propertyAdded : '',
        available_from: availableFrom ? availableFrom : '',
        beds_max: bedsParams.beds_max,
        beds_min: bedsParams.beds_min,
        buyer_incentive: helpToBuy ? 'help_to_buy' : '',
        chain_free: chainFree ? 'true' : '',
        feature: features.join('&feature='),
        include_sold: underOfferOrSold ? 'true' : '',
        is_auction: includeIsAuction ? auction : '',
        is_retirement_home: includeIsRetirementHome ? retirementHome : '',
        is_shared_ownership: !isSharedOwnership ? 'false' : '',
        keywords,
        bills_included: billsIncluded ? billsIncluded : '',
        new_homes: includeNewHomesValue ? newHomes : '',
        furnished_state:
            furnishings && furnishings !== 'Any' ? furnishings : '',
        include_rented: includeLet ? includeLet : '',
        include_shared_accommodation: includeShared === false ? 'false' : '',
        page_size: excludePageSize ? '' : pageSize,
        pets_allowed: petsAllowed ? petsAllowed : '',
        polyenc: getPolyEnc(polyEnc),
        price_frequency: getPriceFrequencyParam(searchType, priceFrequency),
        price_max: priceParams.price_max,
        price_min: priceParams.price_min,
        property_type: propertyType === 'farms_land' ? propertyType : '',
        property_sub_type: getPropertySubType(
            propertyType,
            propertySubType,
            isBungalow
        ),
        duration: duration || '',
        q: locationText || (location.value as string),
        orig_q: locationText ? (location.value as string) : '',
        identifier: locationText ? location.id : '',
        category: locationText ? 'residential' : '',
        section: locationText ? searchType : '',
        radius: excludeRadius ? '' : radius || '0',
        rental_term: shortLets ? 'short' : '',
        reduced_price_only: priceReduced === true ? 'true' : '',
        results_sort: sortOrder,
        search_source: searchSource,
        transport_type: transportType || '',
        user_alert_id: userAlertId || ''
    };

    const result = removeMostFalsyValues(queryParams);
    return result;
};

export const updateResults = async (
    // eslint-disable-next-line
    filters: any,
    router: NextRouter,
    searchType: SearchPageType,
    searchSource: SearchSource,
    sortOrder: string,
    userAlertId: string | null = null,
    excludeRadius = false,
    ignoreLocationText = false,
    page = 1,
    excludePageSize = false
) => {
    const {
        location,
        propertyType,
        propertySubType,
        newHomes,
        retirementHome,
        auction,
        locationText: filterLocationText
    } = filters;

    const { asPath, push, query } = router;

    const newPathBuilder = getPathBuilder(asPath, searchType);

    const isBungalow = propertySubType.includes('bungalow');
    const isPropertyTypeFlatsOrHouses =
        propertyType === 'houses' || propertyType === 'flats';

    const locationText = !ignoreLocationText ? filterLocationText : '';

    // Only need this block if no locationText due to the navigation conditions below
    if (locationText === '') {
        if (newHomes === 'only') {
            newPathBuilder.searchType = 'new-homes';
        }

        if (!newHomes && searchType === 'new-homes') {
            newPathBuilder.searchType = 'for-sale';
        }

        if (!isBungalow && isPropertyTypeFlatsOrHouses) {
            newPathBuilder.propertyType = propertyType as string;
        } else if (isBungalow && propertyType === 'flats') {
            newPathBuilder.propertyType = 'flats';
        } else if (
            isBungalow &&
            propertySubType.length > 1 &&
            propertyType === 'houses'
        ) {
            newPathBuilder.propertyType = 'houses';
        } else if (
            isBungalow &&
            propertySubType.length === 1 &&
            propertyType === 'houses'
        ) {
            newPathBuilder.propertyType = 'bungalows';
        } else {
            newPathBuilder.propertyType = 'property';
        }

        if (!isPropertyTypeFlatsOrHouses && retirementHome === 'true') {
            newPathBuilder.propertyType = 'retirement';
        }

        if (!isPropertyTypeFlatsOrHouses && auction === 'true') {
            if (newPathBuilder.propertyType === 'property') {
                newPathBuilder.propertyType = 'auction';
            } else {
                newPathBuilder.extra.push('auction');
            }
        }

        newPathBuilder.extra.push(location.id);
    }

    const newQuery = updateQueryParams(
        filters,
        searchType,
        searchSource,
        sortOrder,
        isPropertyTypeFlatsOrHouses,
        isBungalow,
        userAlertId,
        excludeRadius,
        locationText,
        newPathBuilder.maps,
        query.polyenc,
        excludePageSize
    );

    const newPath = getNewPath(newPathBuilder);
    const nextQueryParameters = buildQueryParamsString(newQuery, page);

    let nextUrl;
    if (!locationText) {
        nextUrl = buildUrlFromPathAndQueryParams(newPath, nextQueryParameters);
        await push(nextUrl);
    } else {
        nextUrl = buildUrlFromPathAndQueryParams(
            '/search',
            nextQueryParameters
        );
        await push(nextUrl);
    }

    return nextUrl;
};

// House prices search
export const RESULTS_SORT = `results_sort=newest_listings`;
export const SEARCH_SOURCE = `search_source=home`;
export const SECTION = `section=house-prices`;

export const buildHousePricesUrl = (identifier: string, location: string) => {
    if (!identifier) {
        const url = `${MAIN_SITE_LINK}/search/?q=${encodeURIComponent(
            location
        )}&${SECTION}`;
        return url;
    }
    const url = `${MAIN_SITE_LINK}/house-prices/${identifier}?q=${encodeURIComponent(
        location
    )}&${RESULTS_SORT}&${SEARCH_SOURCE}`;

    return url;
};

export const searchHousePrices = (value: string, identifier: string) => {
    const housePricesUrl = buildHousePricesUrl(identifier, value);

    redirect(housePricesUrl);
};

// for-sale and to-rent search
export const searchProperties = (
    userAlertId: string | null,
    router: NextRouter,
    // eslint-disable-next-line
    dispatch: Dispatch<any>,
    searchType: SearchPageType,
    searchSource: SearchSource,
    // eslint-disable-next-line
    filters: any,
    sortOrder: string,
    excludeRadius?: boolean,
    excludePageSize?: boolean
) => {
    if (userAlertId) {
        dispatch({
            type: 'SET_PREVIOUS_SAVED_SEARCH_URI',
            payload: router.asPath
        });
    }

    dispatch({ type: 'SET_SEARCH_SOURCE', payload: searchSource });

    if (userAlertId) {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
            payload: true
        });
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
            payload: null
        });
    }

    const ignoreLocationText = false;
    const page = 1;
    updateResults(
        filters,
        router,
        searchType as SearchPageType,
        searchSource,
        sortOrder,
        userAlertId,
        excludeRadius,
        ignoreLocationText,
        page,
        excludePageSize
    );
};
